package com.siriusxm.pia.player

import contentingestion.unifiedmodel.ReferenceIdentifiable
import kotlinx.coroutines.flow.StateFlow
import kotlin.time.Instant

/**
 * The lifecycle state of a single audio playback session.
 *
 * IDLE means nothing is loaded; LOADING covers both the initial buffering of a track and a mid-stream
 * stall (`waiting`); PLAYING and PAUSED mirror the underlying media element. This is the coarse state
 * the transport UI (and app-installed listeners) switch on — position/duration are reported separately.
 */
public enum class PlayState {
    IDLE,
    PLAYING,
    PAUSED,
    LOADING
}

/** The media clocks supplied to a source's optional presentation-details loader. */
public data class PlaybackClock(
    public val positionMilliseconds: Long,
    public val broadcastTime: Instant? = null,
)

/** Changing presentation fields, separate from the loaded source and its stream credentials. */
public data class PlayingDetails(
    public val entityId: String,
    public val title: String? = null,
    public val subtitle: String? = null,
    public val description: String? = null,
    public val imageUrl: String? = null,
    public val startsAt: Instant? = null,
    public val endsAt: Instant? = null,
    public val approximate: Boolean = false,
    public val relatedEntities: List<ReferenceIdentifiable> = emptyList(),
)

/** Null [pollAfterMilliseconds] means the source does not support details; stop polling it. */
public data class PlaybackDetailsUpdate(
    public val current: PlayingDetails? = null,
    public val pollAfterMilliseconds: Long? = 10_000,
    /** Absolute broadcast intervals; select locally as audio advances instead of on response arrival. */
    public val timeline: List<PlayingDetails>? = null,
)

/** A details lookup's state; independent of whether the audio is playing or paused. */
public enum class PlaybackDetailsStatus {
    LOADING,
    WAITING_FOR_CLOCK,
    AVAILABLE,
    EMPTY,
    UNAVAILABLE,
    UNSUPPORTED,
}

/** Latest details lookup state. Metadata failures do not alter the audio transport state. */
public data class PlaybackDetailsState(
    public val status: PlaybackDetailsStatus = PlaybackDetailsStatus.UNSUPPORTED,
    public val current: PlayingDetails? = null,
)

/**
 * A single playable item driven by a [PlaybackController].
 *
 * [credentialsExpireAt] and [refreshCredentials] support streams whose access depends on short-lived
 * CloudFront signed cookies (UCA-622). When both are present the controller renews the cookies shortly
 * before [credentialsExpireAt] so playback of long episodes — and resuming after a pause past the
 * cookie TTL — keeps working. They are null for streams that need no credential refresh (e.g. direct
 * media URLs), in which case the controller never schedules a refresh.
 *
 * This type is deliberately transport-agnostic: it names a resolved URL and presentation metadata, not
 * an entity id or an SDK request. Turning an entity (or direct details) into an [AudioTrack] — the
 * PreparePlayback round-trip — is the job of the SDK-backed [CMCPlayer] facade, not the engine.
 *
 * [startSeconds] is where playback begins, applied once when the track loads (UCA-1133). It belongs to
 * the track rather than being a `seek` after [PlaybackController.play] because a seek issued before the
 * media is attached (hls.js) or its metadata has loaded (native) is lost. Later seeks, pauses, and
 * resumes are unaffected, and each new track starts at its own offset.
 */
public class AudioTrack(
    public val url: String,
    public val title: String? = null,
    public val subTitle: String? = null,
    public val imageUrl: String? = null,
    public val credentialsExpireAt: Instant? = null,
    public val refreshCredentials: (suspend () -> Instant?)? = null,
    public val startSeconds: Double = 0.0,
    /** Live media suppresses finite-duration and seek controls. */
    public val isLive: Boolean = false,
    /** Optional read-only lookup; the controller scopes polling to this loaded track. */
    public val loadDetails: (suspend (PlaybackClock) -> PlaybackDetailsUpdate)? = null,
)

/**
 * A handle returned when installing an [AudioPlaybackListener]. Call [remove] to detach the listener;
 * it is idempotent (removing an already-removed listener is a no-op).
 */
public fun interface Registration {
    public fun remove()
}

/**
 * A callback surface for apps that prefer notifications over collecting the controller's [StateFlow]s.
 *
 * Every method has a no-op default so an app overrides only the changes it cares about. Callbacks are
 * delivered on the controller's own coroutine scope in response to the corresponding flow emitting;
 * on install, the current value of each flow is delivered once so a late listener is not left blind to
 * the state that already exists.
 */
public interface AudioPlaybackListener {
    /** The coarse playback lifecycle state changed. */
    public fun onStateChanged(state: PlayState) {}

    /** The playback clock advanced (or was sought). Both values are in seconds; duration is 0 until known. */
    public fun onPositionChanged(position: Double, duration: Double) {}

    /** The output volume changed. [volume] is 0..1. */
    public fun onVolumeChanged(volume: Double) {}

    /** A user-facing playback error was raised (non-null) or cleared (null) for the current track. */
    public fun onError(message: String?) {}

    /** The loaded track changed (including to null when playback stops). */
    public fun onTrackChanged(track: AudioTrack?) {}

    /** Presentation details changed without replacing or restarting the loaded track. */
    public fun onDetailsChanged(details: PlaybackDetailsState) {}
}

/**
 * The headless control surface over a single audio playback engine: transport, observable state, and
 * volume, plus app-installable listeners. Implemented by the hls.js-backed engine in
 * cmc-media-player, but named here (in cmc-core) so it can be exposed from
 * [MediaPlayer.createPlayer]'s [CMCPlayer] without cmc-core depending on the player module.
 *
 * All observation is via [StateFlow]s (a Kotlin app can `collect` them directly) or via
 * [addListener]; there is no rendered UI. Transport methods are safe to call before a track is loaded
 * (they are no-ops until [play]).
 */
public interface PlaybackController {
    /** The coarse playback lifecycle state. */
    public val state: StateFlow<PlayState>

    /** The currently loaded track, or null when nothing is loaded. */
    public val track: StateFlow<AudioTrack?>

    /** Changing cut/chapter information for the loaded source, independent of its static metadata. */
    public val details: StateFlow<PlaybackDetailsState>

    /** The current playback position in seconds. */
    public val position: StateFlow<Double>

    /** The current track's duration in seconds, or 0 until it is known. */
    public val duration: StateFlow<Double>

    /** The current output volume, 0..1. */
    public val volume: StateFlow<Double>

    /** A user-facing playback error for the current track, or null when playback is healthy. */
    public val playbackError: StateFlow<String?>

    /** Load and begin playing [track], tearing down any previously loaded track first. */
    public fun play(track: AudioTrack)

    /** Pause the current track (no-op when nothing is loaded). */
    public fun pause()

    /** Resume the current track after a pause (no-op when nothing is loaded). */
    public fun resume()

    /** Stop playback and unload the current track (returns to [PlayState.IDLE]). */
    public fun stop()

    /** Seek to [seconds] within the current track. */
    public fun seek(seconds: Double)

    /** Set the output volume, clamped to 0..1. */
    public fun setVolume(volume: Double)

    /** Mute or unmute output without changing the volume level. */
    public fun setMuted(muted: Boolean)

    /**
     * Install [listener]. The current value of each observable is delivered immediately so the listener
     * is synchronised with existing state. Returns a [Registration] whose [Registration.remove] detaches
     * it.
     */
    public fun addListener(listener: AudioPlaybackListener): Registration

    /**
     * Tear down the engine: stop playback, remove media-element listeners, destroy any hls.js instance,
     * and cancel the credential-refresh loop. After this the controller must not be reused. The global
     * shared bar never disposes its controller; app-owned [CMCPlayer]s must dispose theirs.
     */
    public fun dispose()
}

/**
 * A headless, app-owned player. Resolves playback through the CMC SDK under the hood (no bar rendered),
 * exposes the [controller]'s transport/state/volume surface, and lets the app install its own
 * listeners. Obtain one from [MediaPlayer.createPlayer]; each is independent of the global player bar
 * and of every other [CMCPlayer], and the app that creates it owns its lifecycle and must [dispose] it.
 *
 * The SDK prepare step (PreparePlayback / PrepareDirectPlayback), descriptor-to-[AudioTrack] mapping,
 * and the credential-refresh closure are all handled inside the facade — the caller supplies only the
 * entity id (or direct details), exactly as it would to the shared bar.
 */
public interface CMCPlayer {
    /** The transport + observable-state surface for this player. */
    public val controller: PlaybackController

    /**
     * Resolve [entityId] through the CMC SDK's PreparePlayback and begin playing it, starting
     * [startSeconds] into the media.
     */
    public suspend fun playEntity(entityId: String, startSeconds: Double = 0.0)

    /**
     * Resolve caller-supplied details through the CMC SDK's PrepareDirectPlayback and begin playing.
     * For content that cannot be resolved through Aggregator — most notably an expired/archived AOD
     * episode whose media is still in the bucket. Only `episode-audio` is supported for [type] today.
     * Playback begins [startSeconds] into the media.
     */
    public suspend fun playDetails(
        type: String,
        url: String,
        title: String? = null,
        subTitle: String? = null,
        imageUrl: String? = null,
        durationMs: Long? = null,
        startSeconds: Double = 0.0,
    )

    /** Tear down this player and its underlying [controller]. */
    public fun dispose()
}
